// frontend/src/components/UmamiAnalytics.jsx
// FRONTEND_CODE_VERSION = "v1.0.0"
// Purpose: Load Umami Analytics script for privacy-first web analytics

import { useEffect } from "react";

/**
 * UmamiAnalytics Component
 * 
 * Loads the Umami Analytics script dynamically.
 * Only loads in production or when explicitly enabled via environment variable.
 * 
 * Environment Variables Required:
 * - VITE_UMAMI_WEBSITE_ID: Website ID from Umami dashboard
 * - VITE_UMAMI_SCRIPT_URL: URL to Umami script (e.g., https://umami.example.com/script.js)
 * - VITE_ENABLE_ANALYTICS: Set to "true" to enable (defaults to production only)
 */
const UmamiAnalytics = () => {
  useEffect(() => {
    // Determine if analytics should be enabled
    const shouldEnable =
      import.meta.env.VITE_ENABLE_ANALYTICS === "true" ||
      (import.meta.env.VITE_ENABLE_ANALYTICS !== "false" &&
        import.meta.env.MODE === "production");

    if (!shouldEnable) {
      if (import.meta.env.MODE === "development") {
        console.log("📊 Umami Analytics: Disabled (development mode)");
      }
      return;
    }

    // Get configuration from environment variables
    const websiteId = import.meta.env.VITE_UMAMI_WEBSITE_ID;
    const scriptUrl = import.meta.env.VITE_UMAMI_SCRIPT_URL;

    // Validate required configuration
    if (!websiteId || !scriptUrl) {
      if (import.meta.env.MODE === "development") {
        console.warn(
          "📊 Umami Analytics: Missing configuration. Required: VITE_UMAMI_WEBSITE_ID and VITE_UMAMI_SCRIPT_URL"
        );
      }
      return;
    }

    // Check if script is already loaded
    const existingScript = document.querySelector(
      `script[data-website-id="${websiteId}"]`
    );
    if (existingScript) {
      if (import.meta.env.MODE === "development") {
        console.log("📊 Umami Analytics: Script already loaded");
      }
      return;
    }

    // Create and inject script
    const script = document.createElement("script");
    script.async = true;
    script.defer = true;
    script.setAttribute("data-website-id", websiteId);
    script.src = scriptUrl;

    // Handle script load
    script.onload = () => {
      if (import.meta.env.MODE === "development") {
        console.log("📊 Umami Analytics: Script loaded successfully");
      }
    };

    // Handle script errors
    script.onerror = () => {
      console.error("📊 Umami Analytics: Failed to load script", scriptUrl);
    };

    // Append to document head
    document.head.appendChild(script);

    // Cleanup function
    return () => {
      const scriptToRemove = document.querySelector(
        `script[data-website-id="${websiteId}"]`
      );
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, []);

  // This component doesn't render anything
  return null;
};

export default UmamiAnalytics;

